import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { RosterService } from 'src/app/services/roster/roster.service';
import { AppConstants } from 'src/app/common/app.constants';
import moment from 'moment';

@Component({
  selector: 'app-roster',
  templateUrl: './roster.component.html',
  styleUrls: ['./roster.component.scss'],
  standalone: false
})
export class RosterComponent implements OnInit {
  employeeInfo: any = null;
  employeeId: number = 0;
  currentWeek: number = 0;
  currentYear: number = 0;
  weekStartStr: string = '';
  weekEndStr: string = '';

  shifts: any[] = [];
  filteredShifts: any[] = [];
  uniqueShops: any[] = [];
  selectedShopCode: string = 'ALL';

  taskLegends: any[] = [];
  isLoading = false;

  constructor(
    private router: Router,
    private rosterService: RosterService
  ) {
    const userStr = sessionStorage.getItem(AppConstants.SESSION_VAR_USER);
    if (userStr) {
      this.employeeInfo = JSON.parse(userStr);
      this.employeeId = this.employeeInfo.employee_id;
      this.currentWeek = this.employeeInfo.roster_week;
      this.currentYear = this.employeeInfo.roster_year;
    }
  }

  ngOnInit(): void {
    if (!this.employeeId) {
      this.router.navigate(['/login']);
      return;
    }
    this.loadRoster();
  }

  loadRoster() {
    this.isLoading = true;

    const momentWeek = moment().year(this.currentYear).isoWeek(this.currentWeek);
    const startOfWeek = momentWeek.clone().startOf('isoWeek');
    const endOfWeek = momentWeek.clone().endOf('isoWeek');

    this.weekStartStr = startOfWeek.format('DD MMM');
    this.weekEndStr = endOfWeek.format('DD MMM YYYY');

    this.rosterService.getEmployeeRoster(this.employeeId, this.currentWeek, this.currentYear).subscribe(
      resp => {
        this.isLoading = false;
        if (resp.status === 'SUCCESS') {
          this.shifts = resp.data.shifts || [];
          this.processShifts();
        } else {
          this.shifts = [];
          this.filteredShifts = [];
        }
      },
      err => {
        this.isLoading = false;
        this.shifts = [];
        this.filteredShifts = [];
      }
    );
  }

  processShifts() {
    const shopMap = new Map();
    const tasksMap = new Map();
    let hasSalmonCutting = false;
    let hasInCharge = false;

    this.shifts.forEach(shift => {
      if (shift.shop_code && shift.shop_name) {
        shopMap.set(shift.shop_code, shift.shop_name);
      }

      if (shift.task1_code && shift.task1_name) {
        tasksMap.set(shift.task1_code, {
          name: shift.task1_name,
          bg_color: shift.task1_bg_color,
          fg_color: shift.task1_fg_color
        });
      }
      if (shift.task2_code && shift.task2_name) {
        tasksMap.set(shift.task2_code, {
          name: shift.task2_name,
          bg_color: shift.task2_bg_color,
          fg_color: shift.task2_fg_color
        });
      }
      if (shift.task3_code && shift.task3_name) {
        tasksMap.set(shift.task3_code, {
          name: shift.task3_name,
          bg_color: shift.task3_bg_color,
          fg_color: shift.task3_fg_color
        });
      }

      if (shift.is_salmon_cutting === '1' || shift.is_salmon_cutting === 1) {
        hasSalmonCutting = true;
      }
      if (shift.is_in_charge === '1' || shift.is_in_charge === 1) {
        hasInCharge = true;
      }
    });

    if (hasSalmonCutting) {
      tasksMap.set('SC', {
        name: 'Salmon Cutting',
        bg_color: '#991b1b',
        fg_color: '#fecaca'
      });
    }
    if (hasInCharge) {
      tasksMap.set('IC', {
        name: 'In Charge',
        bg_color: '#6b21a8',
        fg_color: '#e9d5ff'
      });
    }

    this.uniqueShops = Array.from(shopMap.entries()).map(([code, name]) => ({ code, name }));
    if (this.uniqueShops.length === 1) {
      this.selectedShopCode = this.uniqueShops[0].code;
    } else if (this.selectedShopCode !== 'ALL' && !this.uniqueShops.some(s => s.code === this.selectedShopCode)) {
      this.selectedShopCode = 'ALL';
    }
    const legends = Array.from(tasksMap.entries()).map(([code, data]: [string, any]) => ({
      code,
      name: data.name,
      bg_color: data.bg_color,
      fg_color: data.fg_color
    }));

    const remainder = legends.length % 6;
    if (remainder !== 0) {
      const padCount = 6 - remainder;
      for (let i = 0; i < padCount; i++) {
        legends.push({ code: '', name: '', bg_color: '', fg_color: '' });
      }
    }
    this.taskLegends = legends;

    this.applyShopFilter();
  }

  applyShopFilter() {
    let list = this.shifts;
    if (this.selectedShopCode !== 'ALL') {
      list = this.shifts.filter(s => s.shop_code === this.selectedShopCode);
    }

    list.sort((a, b) => {
      const dateCmp = a.shift_date.localeCompare(b.shift_date);
      if (dateCmp !== 0) return dateCmp;
      return a.start_time.localeCompare(b.start_time);
    });

    this.filteredShifts = list.map(shift => {
      const shiftDate = moment(shift.shift_date);

      let hours = 0;
      if (shift.start_time && shift.end_time) {
        const start = moment(shift.shift_date + ' ' + shift.start_time);
        const end = moment(shift.shift_date + ' ' + shift.end_time);
        if (end.isBefore(start)) {
          end.add(1, 'day');
        }
        hours = end.diff(start, 'minutes') / 60;
      }

      const notesArr = [];
      if (shift.task1_name) notesArr.push(shift.task1_name);
      if (shift.task2_name) notesArr.push(shift.task2_name);
      if (shift.task3_name) notesArr.push(shift.task3_name);
      if (Number(shift.is_salmon_cutting) === 1) notesArr.push('Salmon Cutting');
      if (Number(shift.is_in_charge) === 1) notesArr.push('In Charge');

      return {
        ...shift,
        dayStr: shiftDate.format('DD'),
        monthStr: shiftDate.format('MMM'),
        dayName: shiftDate.format('ddd'),
        startTimeStr: shift.start_time ? shift.start_time.substring(0, 5) : '',
        endTimeStr: shift.end_time ? shift.end_time.substring(0, 5) : '',
        hoursStr: hours.toFixed(2) + 'h',
        notesJoined: notesArr.join(', '),
        comment: shift.notes || ''
      };
    });
  }

  prevWeek() {
    this.currentWeek--;
    if (this.currentWeek < 1) {
      this.currentWeek = 52;
      this.currentYear--;
    }
    this.loadRoster();
  }

  nextWeek() {
    this.currentWeek++;
    if (this.currentWeek > 52) {
      this.currentWeek = 1;
      this.currentYear++;
    }
    this.loadRoster();
  }

  logout() {
    sessionStorage.removeItem(AppConstants.SESSION_VAR_USER);
    sessionStorage.removeItem(AppConstants.SESSION_VAR_TOKEN);
    this.router.navigate(['/login'], { queryParamsHandling: 'preserve' });
  }

  getSelectedShopName(): string {
    if (this.selectedShopCode === 'ALL') {
      return 'All Shops';
    }
    const shop = this.uniqueShops.find(s => s.code === this.selectedShopCode);
    return shop ? shop.name : 'All Shops';
  }

  isShopDropdownOpen = false;

  toggleShopDropdown() {
    this.isShopDropdownOpen = !this.isShopDropdownOpen;
  }

  selectShop(code: string) {
    this.selectedShopCode = code;
    this.isShopDropdownOpen = false;
    this.applyShopFilter();
  }
}
